import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { medicPortalAPI } from '../../lib/api';
import { fmt } from '../../lib/currency';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { 
  CalendarDays, 
  Clock,
  MapPin,
  ArrowLeft,
  PoundSterling,
  Briefcase,
  Building,
  FileText,
  Phone,
  CheckCircle,
  XCircle,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { format, differenceInHours, parseISO } from 'date-fns';
import ShiftCheckIn from '../../components/portal/ShiftCheckIn';

const STATUS_STYLES = {
  pending: 'bg-[#FFC04D]/15 text-[#FFC04D] border-[#FFC04D]/20',
  confirmed: 'bg-[#1ABC9C]/15 text-[#1ABC9C] border-[#1ABC9C]/20',
  standby: 'bg-indigo-500/15 text-indigo-400 border-indigo-500/20',
  completed: 'bg-gray-500/15 text-gray-400 border-gray-500/20',
  cancelled: 'bg-red-500/15 text-red-400 border-red-500/20',
};

const TYPE_STYLES = {
  regular: 'bg-[#1ABC9C]/15 text-[#1ABC9C]',
  night: 'bg-indigo-500/15 text-indigo-400',
  standby: 'bg-[#FFC04D]/15 text-[#FFC04D]',
  emergency: 'bg-red-500/15 text-red-400',
};

export default function ShiftDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [shift, setShift] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchShift = useCallback(async () => {
    try {
      const response = await medicPortalAPI.getShiftDetails(id);
      setShift(response.data);
    } catch (error) {
      toast.error('Failed to load shift details');
      navigate('/portal/shifts');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchShift();
  }, [fetchShift]);

  // Scroll to Check-In section when linked with `#checkin` hash. Deep links
  // from the dashboard/shifts list pass the hash so medics land directly on
  // the Check-In CTA (iter71).
  useEffect(() => {
    if (loading || !shift) return;
    if (typeof window === 'undefined') return;
    if (window.location.hash !== '#checkin') return;
    const el = document.getElementById('checkin');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [loading, shift]);

  const calculateHours = () => {
    if (!shift) return 0;
    try {
      const start = parseISO(`${shift.date}T${shift.start_time}`);
      const end = parseISO(`${shift.date}T${shift.end_time}`);
      return differenceInHours(end, start);
    } catch {
      return 0;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-[#1ABC9C]/20 border-t-[#1ABC9C] rounded-full animate-spin" />
      </div>
    );
  }

  if (!shift) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-[#FFC04D]" />
        <p className="text-[#A1A1AA]">Shift not found</p>
        <Button 
          variant="outline" 
          className="mt-4"
          onClick={() => navigate('/portal/shifts')}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Shifts
        </Button>
      </div>
    );
  }

  const hours = calculateHours();
  // Compare calendar dates (not midnight-UTC timestamps) so today's shift
  // still counts as upcoming; also keep in-progress shifts visible for check-out.
  const isUpcoming =
    (shift.date || '').substring(0, 10) >= format(new Date(), 'yyyy-MM-dd') ||
    (shift.checked_in_at && !shift.checked_out_at);

  return (
    <div className="space-y-6" data-testid="shift-details-container">
      {/* Back Button */}
      <Button 
        variant="ghost" 
        className="text-[#A1A1AA] hover:text-[#F2F2F2]"
        onClick={() => navigate('/portal/shifts')}
        data-testid="back-to-shifts-btn"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Shifts
      </Button>

      {/* Header Card */}
      <Card className="bg-gradient-to-r from-[#1ABC9C]/20 to-[#1ABC9C]/5 border-[#1ABC9C]/20">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Badge className={STATUS_STYLES[shift.status]}>
                  {shift.status === 'confirmed' && <CheckCircle className="w-3 h-3 mr-1" />}
                  {shift.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                  {shift.status === 'cancelled' && <XCircle className="w-3 h-3 mr-1" />}
                  {shift.status}
                </Badge>
                <Badge className={`${TYPE_STYLES[shift.shift_type]} border-0`}>
                  {shift.shift_type}
                </Badge>
              </div>
              <h1 className="text-2xl font-bold text-[#F2F2F2] font-['Manrope']">
                {format(new Date(shift.date), 'EEEE, MMMM dd, yyyy')}
              </h1>
              <p className="text-[#A1A1AA] mt-1 font-['JetBrains_Mono']">
                {shift.start_time} - {shift.end_time} ({hours} hours)
              </p>
            </div>
            
            <div className="text-right">
              <p className="text-sm text-[#A1A1AA]">Your Earnings</p>
              <p className="text-3xl font-bold text-[#1ABC9C] font-['JetBrains_Mono']">
                {fmt((Number(shift.rate_payable) || 0) * (parseFloat(hours) || 0), shift.currency)}
              </p>
              <p className="text-xs text-[#A1A1AA] mt-1">
                {fmt(shift.rate_payable, shift.currency)}/hr × {hours}h
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Location & Time */}
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardHeader>
            <CardTitle className="text-[#F2F2F2] font-['Manrope'] flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#1ABC9C]" />
              Location & Schedule
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-[#1A1A1A] rounded-lg border border-white/5">
              <div className="flex items-start gap-3">
                <Building className="w-5 h-5 text-[#A1A1AA] mt-0.5" />
                <div>
                  <p className="text-sm text-[#A1A1AA]">Location</p>
                  <p className="text-[#F2F2F2] font-medium">{shift.location}</p>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-[#1A1A1A] rounded-lg border border-white/5">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-4 h-4 text-[#1ABC9C]" />
                  <p className="text-sm text-[#A1A1AA]">Start Time</p>
                </div>
                <p className="text-lg font-bold text-[#F2F2F2] font-['JetBrains_Mono']">
                  {shift.start_time}
                </p>
              </div>
              <div className="p-4 bg-[#1A1A1A] rounded-lg border border-white/5">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-4 h-4 text-[#FFC04D]" />
                  <p className="text-sm text-[#A1A1AA]">End Time</p>
                </div>
                <p className="text-lg font-bold text-[#F2F2F2] font-['JetBrains_Mono']">
                  {shift.end_time}
                </p>
              </div>
            </div>

            <div className="p-4 bg-[#1A1A1A] rounded-lg border border-white/5">
              <div className="flex items-center gap-2 mb-1">
                <CalendarDays className="w-4 h-4 text-[#1ABC9C]" />
                <p className="text-sm text-[#A1A1AA]">Total Duration</p>
              </div>
              <p className="text-lg font-bold text-[#F2F2F2]">{hours} hours</p>
            </div>
          </CardContent>
        </Card>

        {/* Job & Payment Info */}
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardHeader>
            <CardTitle className="text-[#F2F2F2] font-['Manrope'] flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-[#FFC04D]" />
              Job & Payment Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {shift.job_type_name && (
              <div className="p-4 bg-[#1A1A1A] rounded-lg border border-white/5">
                <div className="flex items-start gap-3">
                  <Briefcase className="w-5 h-5 text-[#A1A1AA] mt-0.5" />
                  <div>
                    <p className="text-sm text-[#A1A1AA]">Job Type</p>
                    <p className="text-[#F2F2F2] font-medium">{shift.job_type_name}</p>
                  </div>
                </div>
              </div>
            )}

            {shift.contract_name && (
              <div className="p-4 bg-[#1A1A1A] rounded-lg border border-white/5">
                <div className="flex items-start gap-3">
                  <FileText className="w-5 h-5 text-[#A1A1AA] mt-0.5" />
                  <div>
                    <p className="text-sm text-[#A1A1AA]">Contract</p>
                    <p className="text-[#F2F2F2] font-medium">{shift.contract_name}</p>
                  </div>
                </div>
              </div>
            )}

            <Separator className="bg-white/10" />

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-[#1A1A1A] rounded-lg border border-white/5">
                <div className="flex items-center gap-2 mb-1">
                  <PoundSterling className="w-4 h-4 text-[#1ABC9C]" />
                  <p className="text-sm text-[#A1A1AA]">Your Pay</p>
                </div>
                <p className="text-xl font-bold text-[#1ABC9C] font-['JetBrains_Mono']">
                  {fmt(shift.rate_payable, shift.currency)}
                </p>
              </div>
              <div className="p-4 bg-[#1A1A1A] rounded-lg border border-white/5">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-4 h-4 text-[#A1A1AA]" />
                  <p className="text-sm text-[#A1A1AA]">Hourly Rate</p>
                </div>
                <p className="text-xl font-bold text-[#F2F2F2] font-['JetBrains_Mono']">
                  {hours > 0 ? `${fmt((shift.rate_payable || 0) / hours, shift.currency)}/hr` : fmt(0, shift.currency) + '/hr'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Notes Section */}
      {shift.notes && (
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardHeader>
            <CardTitle className="text-[#F2F2F2] font-['Manrope'] flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#A1A1AA]" />
              Notes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-[#A1A1AA] whitespace-pre-wrap">{shift.notes}</p>
          </CardContent>
        </Card>
      )}

      {/* GPS Check-in Section */}
      {isUpcoming && (
        <div id="checkin">
          <ShiftCheckIn 
            shift={shift} 
            onStatusChange={fetchShift}
          />
        </div>
      )}

      {/* Contact Info (for upcoming shifts) */}
      {isUpcoming && shift.status !== 'cancelled' && (
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardHeader>
            <CardTitle className="text-[#F2F2F2] font-['Manrope'] flex items-center gap-2">
              <Phone className="w-5 h-5 text-[#1ABC9C]" />
              Need Help?
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-[#A1A1AA]">
              If you have any questions about this shift or need to make changes, 
              please contact the scheduling team.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
